/**
 * Handles Capacitor push notification setup and deep-link navigation.
 * BP-074 FIX: Must be rendered INSIDE <BrowserRouter> for useNavigate() access.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { initCapacitor, onPushTapped, isNative, registerForPush } from '@/lib/capacitor';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { Capacitor } from '@capacitor/core';

export function PushNotificationHandler() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const registeredRef = useRef(false);

  // Initialize Capacitor (runs once)
  useEffect(() => {
    initCapacitor().catch(console.error);
  }, []);

  // Register for push notifications after successful authentication
  useEffect(() => {
    if (!isNative || status !== 'authenticated' || registeredRef.current) return;
    registeredRef.current = true;

    (async () => {
      try {
        const token = await registerForPush();
        if (token) {
          // Save token for logout unsubscribe (BP-060)
          localStorage.setItem('__pa_push_token', token);
          await api.post('/api/notifications/subscribe', {
            token,
            platform: Capacitor.getPlatform(),
            app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
          });
        }
      } catch (err) {
        console.warn('[PushNotificationHandler] Push registration failed:', err);
      }
    })();
  }, [status]);

  // Handle push notification taps → deep link navigation
  useEffect(() => {
    if (!isNative) return;
    // BP-059 FIX: Pass job_id in URL so cross-device sessions work.
    return onPushTapped((data) => {
      if (data.job_id) navigate(`/research-workflow?jobId=${data.job_id}`);
      // Alert pushes carry an explicit deep_link (in-app paths only); prefer it.
      else if (data.deep_link?.startsWith('/')) navigate(data.deep_link);
      else if (data.type === 'catalyst' || data.type === 'iv_entry') navigate('/catalysts');
      else if (data.type === 'watchdog') navigate('/trades');
    });
  }, [navigate]);

  return null; // Invisible component — only provides side effects
}
